// @flow

import * as React from 'react';

import { CornerDownRight } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { getCart, removeCartBundle, type CartBundle } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Badge, Base, Fqdn, Tld } from '@gandi/ui';

import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { ListRow } from '../../ListRow';

import DomainBundleDetailRow from './DomainBundleDetailRow';
import ProductItemBase from './components/ProductItemBase';
import { FreeMailboxRow, TitleRow, TotalRow } from './components/Rows';
import Price from './components/Rows/Price';

type Props = {
  bundle: CartBundle,
};

export function DomainBundleRow({ bundle }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  const removeBundle: (
    evt: SyntheticEvent<HTMLButtonElement>,
  ) => Promise<void> = async (evt: SyntheticEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    try {
      await dispatch(removeCartBundle(bundle.id));
    } catch (err) {
      const message = __('Unable to remove the bundle from the cart.');
      dispatch(notifyDanger({ message }));
    } finally {
      await dispatch(getCart()).catch(() => {});
    }
  };

  const tlds = bundle.products
    .map(domain => `.${domain.payload.tld || ''}`)
    .join(', ');

  const hasTaxes = bundle.priceBeforeTaxes !== bundle.priceAfterTaxes;

  if (FF_CART_OVERVIEW_NDS) {
    const label = bundle.products.map((domain, i) =>
      i === 0 ? (
        <Fqdn>{domain.payload.label || domain.payload.fqdn || ''}</Fqdn>
      ) : (
        <>
          {' '}
          + <Tld>{domain.payload.tld || ''}</Tld>
        </>
      ),
    );

    return (
      <ProductItemBase
        header={
          <TitleRow
            label={label}
            productType={bundle.products[0].productType}
            handleRemoveAction={removeBundle}
          />
        }
        footer={
          <TotalRow hasTaxes={hasTaxes} totalPrice={bundle.priceBeforeTaxes} />
        }
      >
        <Base flex flexCol gap="xxsmall">
          <FreeMailboxRow bundle={bundle.products} />
          {bundle.products.map(product => {
            return (
              <ListRow
                key={product.id}
                intent="muted"
                icon={<CornerDownRight size={16} strokeWidth={1.25} />}
                title={product.label || ''}
                badge={
                  product.total?.before_discount ? (
                    <Badge intent="promotional">{__('Discount')}</Badge>
                  ) : undefined
                }
                price={
                  <Price
                    price={product.total?.before_taxes}
                    priceCrossedOut={product.total?.before_discount}
                  />
                }
              />
            );
          })}
        </Base>
      </ProductItemBase>
    );
  }

  const legacyLabel = __('%(tlds)s bundle - 1 year', { tlds });

  // Legacy style
  return (
    <Base
      backgroundColor="white"
      paddingX="small"
      paddingBottom="small"
      flex
      flexCol
    >
      <TitleRow
        label={legacyLabel}
        productType={bundle.products[0].productType}
        handleRemoveAction={removeBundle}
      />
      {bundle.products.map((product, i) => {
        return (
          <DomainBundleDetailRow
            key={`bundle-product-${product.label || ''}`}
            product={product}
            isLast={i === bundle.products.length - 1}
          />
        );
      })}
      <TotalRow hasTaxes={hasTaxes} totalPrice={bundle.priceBeforeTaxes} />
    </Base>
  );
}
